import type { TAccount, TCluster, TDialerList, TLastRing, TPreferences, TSmartView, TUserStatus } from "@/schemas/user.schema"
import type { ConnectionState } from "twilio-sync"
import { observable } from "@legendapp/state"
import { synced } from "@legendapp/state/sync"
import { ObservablePersistLocalStorage } from "@legendapp/state/persist-plugins/local-storage"
import consola from "consola"
import type { IRingioNameSpace } from "@/sync/syncTypes"
import { clearRosterStore } from "@/stores/roster.store"

const STORE_PERSIST_KEY = "syncStore_v4"

interface SyncData {
  version: number
  status: ConnectionState
  userStatus?: TUserStatus
  account?: TAccount
  cluster?: TCluster
  userPreferences?: TPreferences
  /**
   * @deprecated Use `usePowerDialerStore` from `@/stores/powerDialer/store` instead.
   * This field is kept for sync handler compatibility but should not be read from directly.
   * Access dialer list data via `usePowerDialerStore((state) => state.cachedDialerListItems)`.
   */
  dialerList?: TDialerList
  smartView?: TSmartView
  lastRing?: TLastRing
  temp: {
    selectedContactID?: string
  }
}

const initialState: SyncData = {
  version: 2,
  status: "disconnected",
  userStatus: undefined,
  account: undefined,
  cluster: undefined,
  userPreferences: undefined,
  dialerList: undefined,
  smartView: undefined,
  lastRing: undefined,
  temp: {
    selectedContactID: undefined,
  },
}

const initialSyncClientState: IRingioNameSpace = {
  twilio: {
    sync: {
      client: undefined,
      initialization: {
        documents: {
          account: undefined,
          departmentStats: undefined,
          dialerList: undefined,
          intention: undefined,
          lastRing: undefined,
          paymentStatus: undefined,
          roster: undefined,
          smartView: undefined,
          userPreferences: undefined,
          userStats: undefined,
          userStatus: undefined,
          cluster: undefined,
        },
        maps: {
          contacts: undefined,
          smsHistories: undefined,
          liveCalls: undefined,
          roster: undefined,
        },
        streams: {
          recentCalls: undefined,
        },
      },
    },
  },
}

if (typeof window !== "undefined") {
  window.ringioFrontV3 = initialSyncClientState
}

interface SyncStore extends SyncData {
  setStatus: (status: ConnectionState) => void
  clear: () => void
}

export const syncStore$ = observable<SyncStore>(
  synced({
    initial: {
      ...initialState,
      setStatus: (status: ConnectionState) => {
        syncStore$.status.set(status)
      },
      clear: async () => {
        consola.info("Clearing sync store")
        if (typeof window !== "undefined") {
          if (window.ringioFrontV3.twilio.sync.client) {
            consola.info("Disconnecting Twilio Sync client")
            try {
              await window.ringioFrontV3.twilio.sync.client?.shutdown()
            } catch (error) {
              consola.error("Error shutting down Twilio Sync client", error)
            }
          }

          consola.info("Clearing global window.ringioFrontV3")
          window.ringioFrontV3 = Object.assign({}, initialSyncClientState) // more efficient than spreading
        }

        consola.info("Assigning initial state to the sync store")
        syncStore$.assign(initialState)
        clearRosterStore()

        consola.info("Sync store cleared")
      },
    },
    persist: {
      name: STORE_PERSIST_KEY,
      plugin: ObservablePersistLocalStorage,
    },
  })
)
